import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Table, Thead, Tbody, Tr, Th, Td, Badge, Icon, HStack, Divider } from "@chakra-ui/react";
import { FaSolarPanel, FaMicrochip, FaBolt, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";

const SolarResourcesTechnology: React.FC = () => {
  const panelComparison = [
    { feature: "Efficiency", mono: "20-22%", poly: "15-17%", thin: "10-12%" },
    { feature: "Cost per Watt", mono: "$0.80-1.20", poly: "$0.70-1.00", thin: "$0.60-0.90" },
    { feature: "Lifespan", mono: "25-30 years", poly: "25-28 years", thin: "20-25 years" },
    { feature: "Temperature Coefficient", mono: "-0.3 to -0.4%/°C", poly: "-0.4 to -0.5%/°C", thin: "-0.2 to -0.3%/°C" },
    { feature: "Space Required", mono: "Lower", poly: "Higher", thin: "Highest" },
    { feature: "Low Light Performance", mono: "Excellent", poly: "Good", thin: "Very Good" }
  ];

  const inverterComparison = [
    { feature: "Cost", string: "$1,500-3,000", micro: "$3,000-5,000", hybrid: "$4,000-7,000" },
    { feature: "Efficiency", string: "96-98%", micro: "95-96%", hybrid: "96-98%" },
    { feature: "Panel-Level Monitoring", string: "No", micro: "Yes", hybrid: "Yes" },
    { feature: "Shading Tolerance", string: "Low", micro: "High", hybrid: "Medium-High" },
    { feature: "Scalability", string: "Limited", micro: "Excellent", hybrid: "Good" },
    { feature: "Battery Compatible", string: "No", micro: "No", hybrid: "Yes" }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/resources/technology"
        fallbackTitle="Solar Technology Guide"
        fallbackDescription="Understanding solar technology and system components"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaMicrochip} boxSize={12} />
            <Heading size="2xl" fontWeight="bold">
              Solar Technology Comparison
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="3xl">
              Understanding the different types of solar panels, inverters, and system components
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Panel Types Comparison */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Badge bg={SOLAR_GOLD} color="white" fontSize="md" px={4} py={2}>PANEL TYPES</Badge>
          <Heading size="xl" textAlign="center">Solar Panel Technologies</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
            Compare the three main types of solar panels available today
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mb={12}>
          <Box
            p={8}
            bg="gray.50"
            borderRadius="xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
            shadow="md"
          >
            <Icon as={FaSolarPanel} boxSize={12} color={SOLAR_GOLD} mb={4} />
            <Heading size="md" mb={3} color={NAVY}>Monocrystalline</Heading>
            <Text color="gray.700" fontSize="sm" mb={4}>
              Made from single-crystal silicon, these panels are the most efficient and space-effective option.
              They have a distinctive black appearance and perform well in low-light conditions.
            </Text>
            <VStack align="stretch" spacing={2}>
              <HStack><Icon as={FaCheckCircle} color={SOLAR_GOLD} /><Text fontSize="sm">Highest efficiency</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={SOLAR_GOLD} /><Text fontSize="sm">Best in limited space</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={SOLAR_GOLD} /><Text fontSize="sm">Longest lifespan</Text></HStack>
              <HStack><Icon as={FaTimesCircle} color="red.500" /><Text fontSize="sm">Higher upfront cost</Text></HStack>
            </VStack>
          </Box>

          <Box
            p={8}
            bg="gray.50"
            borderRadius="xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
            shadow="md"
          >
            <Icon as={FaSolarPanel} boxSize={12} color={SOLAR_GOLD} mb={4} />
            <Heading size="md" mb={3} color={NAVY}>Polycrystalline</Heading>
            <Text color="gray.700" fontSize="sm" mb={4}>
              Made from multiple silicon fragments, these panels have a blue-speckled appearance.
              They offer good performance at a lower price point.
            </Text>
            <VStack align="stretch" spacing={2}>
              <HStack><Icon as={FaCheckCircle} color={SOLAR_GOLD} /><Text fontSize="sm">Lower cost</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={SOLAR_GOLD} /><Text fontSize="sm">Good efficiency</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={SOLAR_GOLD} /><Text fontSize="sm">Proven technology</Text></HStack>
              <HStack><Icon as={FaTimesCircle} color="red.500" /><Text fontSize="sm">Needs more space</Text></HStack>
            </VStack>
          </Box>

          <Box
            p={8}
            bg="gray.50"
            borderRadius="xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
            shadow="md"
          >
            <Icon as={FaSolarPanel} boxSize={12} color={SOLAR_GOLD} mb={4} />
            <Heading size="md" mb={3} color={NAVY}>Thin-Film</Heading>
            <Text color="gray.700" fontSize="sm" mb={4}>
              Made by depositing thin layers of photovoltaic material. They're flexible, lightweight,
              and perform well in high temperatures.
            </Text>
            <VStack align="stretch" spacing={2}>
              <HStack><Icon as={FaCheckCircle} color={SOLAR_GOLD} /><Text fontSize="sm">Flexible & lightweight</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={SOLAR_GOLD} /><Text fontSize="sm">Heat tolerant</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={SOLAR_GOLD} /><Text fontSize="sm">Lowest cost</Text></HStack>
              <HStack><Icon as={FaTimesCircle} color="red.500" /><Text fontSize="sm">Lower efficiency</Text></HStack>
            </VStack>
          </Box>
        </SimpleGrid>

        {/* Panel Comparison Table */}
        <Box overflowX="auto" bg="gray.50" p={6} borderRadius="xl" border="2px solid" borderColor="gray.200">
          <Table variant="simple" size="md">
            <Thead bg="gray.200">
              <Tr>
                <Th>Feature</Th>
                <Th>Monocrystalline</Th>
                <Th>Polycrystalline</Th>
                <Th>Thin-Film</Th>
              </Tr>
            </Thead>
            <Tbody>
              {panelComparison.map((row, index) => (
                <Tr key={index} bg={index % 2 === 0 ? "white" : "gray.50"}>
                  <Td fontWeight="semibold">{row.feature}</Td>
                  <Td>{row.mono}</Td>
                  <Td>{row.poly}</Td>
                  <Td>{row.thin}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Container>

      <Divider />

      {/* Inverter Types Comparison */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Badge bg={SOLAR_GOLD} color="white" fontSize="md" px={4} py={2}>INVERTER TYPES</Badge>
          <Heading size="xl" textAlign="center">Solar Inverter Technologies</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
            The inverter is the brain of your solar system - here's how to choose the right one
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mb={12}>
          <Box
            p={8}
            bg="gray.50"
            borderRadius="xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
            shadow="md"
          >
            <Icon as={FaBolt} boxSize={12} color={SOLAR_GOLD} mb={4} />
            <Heading size="md" mb={3} color={NAVY}>String Inverter</Heading>
            <Text color="gray.700" fontSize="sm" mb={4}>
              Traditional central inverter that converts DC power from all panels in a series.
              Most cost-effective for systems without shading issues.
            </Text>
            <Badge bg={NAVY} color="white" mb={3}>Best for: Simple roofs without shading</Badge>
          </Box>

          <Box
            p={8}
            bg="gray.50"
            borderRadius="xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
            shadow="md"
          >
            <Icon as={FaBolt} boxSize={12} color={SOLAR_GOLD} mb={4} />
            <Heading size="md" mb={3} color={NAVY}>Microinverters</Heading>
            <Text color="gray.700" fontSize="sm" mb={4}>
              Individual inverters attached to each panel, allowing independent operation.
              Optimal for complex roofs and partial shading situations.
            </Text>
            <Badge bg={NAVY} color="white" mb={3}>Best for: Complex roofs with shading</Badge>
          </Box>

          <Box
            p={8}
            bg="gray.50"
            borderRadius="xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
            shadow="md"
          >
            <Icon as={FaBolt} boxSize={12} color={SOLAR_GOLD} mb={4} />
            <Heading size="md" mb={3} color={NAVY}>Hybrid Inverter</Heading>
            <Text color="gray.700" fontSize="sm" mb={4}>
              Advanced inverter that manages both solar panels and battery storage.
              Essential for systems with batteries or future battery plans.
            </Text>
            <Badge bg={NAVY} color="white" mb={3}>Best for: Battery-ready systems</Badge>
          </Box>
        </SimpleGrid>

        {/* Inverter Comparison Table */}
        <Box overflowX="auto" bg="gray.50" p={6} borderRadius="xl" border="2px solid" borderColor="gray.200">
          <Table variant="simple" size="md">
            <Thead bg="gray.200">
              <Tr>
                <Th>Feature</Th>
                <Th>String Inverter</Th>
                <Th>Microinverters</Th>
                <Th>Hybrid Inverter</Th>
              </Tr>
            </Thead>
            <Tbody>
              {inverterComparison.map((row, index) => (
                <Tr key={index} bg={index % 2 === 0 ? "white" : "gray.50"}>
                  <Td fontWeight="semibold">{row.feature}</Td>
                  <Td>{row.string}</Td>
                  <Td>{row.micro}</Td>
                  <Td>{row.hybrid}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Container>

      {/* Key Takeaways */}
      <Box bg={brandConfig.colors.primary} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <Heading size="xl" textAlign="center">Key Takeaways</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
              <Box p={6} bg="whiteAlpha.200" borderRadius="lg">
                <Heading size="sm" mb={3}>For Maximum Efficiency</Heading>
                <Text fontSize="sm" opacity={0.9}>
                  Choose monocrystalline panels with microinverters if you have limited roof space
                  or shading issues. This combination maximizes energy production.
                </Text>
              </Box>
              <Box p={6} bg="whiteAlpha.200" borderRadius="lg">
                <Heading size="sm" mb={3}>For Best Value</Heading>
                <Text fontSize="sm" opacity={0.9}>
                  Polycrystalline panels with a string inverter offer excellent value if you have
                  ample roof space and minimal shading concerns.
                </Text>
              </Box>
              <Box p={6} bg="whiteAlpha.200" borderRadius="lg">
                <Heading size="sm" mb={3}>For Future-Proofing</Heading>
                <Text fontSize="sm" opacity={0.9}>
                  A hybrid inverter allows you to add battery storage later without replacing
                  your inverter, protecting your investment.
                </Text>
              </Box>
              <Box p={6} bg="whiteAlpha.200" borderRadius="lg">
                <Heading size="sm" mb={3}>For Complex Installations</Heading>
                <Text fontSize="sm" opacity={0.9}>
                  Microinverters shine on multi-directional roofs or locations with trees,
                  chimneys, or other shading obstacles.
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarResourcesTechnology;
